import { useId } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const WIDTH = 104
const HEIGHT = 64
const RADIUS = 40
const STROKE = 10
const CENTER_X = WIDTH / 2
const BASELINE = 52
const ARC_LENGTH = Math.PI * RADIUS

const ARC = `M ${CENTER_X - RADIUS} ${BASELINE} A ${RADIUS} ${RADIUS} 0 0 1 ${CENTER_X + RADIUS} ${BASELINE}`

/**
 * @param color  a palette colour for the arc. The default gradient reads high-is-good, which is
 *               backwards for a risk measure — passing a colour lets the same gauge carry severity.
 */
export default function Gauge({ value, max, unit, caption, display, color, size = 1 }) {
  const theme = useTheme()
  const gradientId = useId()

  const bounded = max !== null && max !== undefined && Number(max) > 0
  const stroke = color ? color : `url(#${gradientId})`
  const ratio = bounded ? Math.min(1, Math.max(0, Number(value) / Number(max))) : 0

  return (
    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
      <Box sx={{ position: 'relative', width: WIDTH * size, height: HEIGHT * size }}>
        <svg width={WIDTH * size} height={HEIGHT * size} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={theme.palette.warning.light} />
              <stop offset="50%" stopColor={theme.palette.success.main} />
              <stop offset="100%" stopColor={theme.palette.info.main} />
            </linearGradient>
          </defs>

          <path
            d={ARC}
            fill="none"
            stroke={theme.palette.action.disabledBackground}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />

          {bounded && ratio > 0 && (
            <path
              d={ARC}
              fill="none"
              stroke={stroke}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={ARC_LENGTH}
              strokeDashoffset={ARC_LENGTH * (1 - ratio)}
              style={{ transition: 'stroke-dashoffset .6s ease' }}
            />
          )}
        </svg>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            pb: 0.25,
            pointerEvents: 'none',
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: 20 * size, fontWeight: 700, lineHeight: 1, color: 'text.primary', whiteSpace: 'nowrap' }}
          >
            {display}
            {unit && (
              <Box component="span" sx={{ fontSize: 14 * size, fontWeight: 700 }}>
                {unit}
              </Box>
            )}
          </Typography>
        </Box>
      </Box>

      {caption && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
          {caption}
        </Typography>
      )}
    </Box>
  )
}
